/**
 * Centralized Date Utility
 * Provides consistent timezone handling across the application
 *
 * By default, uses the user's browser timezone.
 * Can be configured to use a specific timezone (e.g., IST).
 */

/**
 * Get the user's preferred timezone from localStorage or browser
 * @returns {string} IANA timezone identifier (e.g., 'Asia/Kolkata', 'America/New_York')
 */
export const getUserTimezone = () => {
  // Check if user has set a preferred timezone in settings
  const savedTimezone = localStorage.getItem('userTimezone');
  if (savedTimezone) {
    return savedTimezone;
  }

  // Otherwise, use browser's timezone
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Set user's preferred timezone
 * @param {string} timezone - IANA timezone identifier (e.g., 'Asia/Kolkata')
 */
export const setUserTimezone = (timezone) => {
  if (timezone) {
    localStorage.setItem('userTimezone', timezone);
  } else {
    localStorage.removeItem('userTimezone');
  }
};

/**
 * Common timezone options
 */
export const TIMEZONES = {
  IST: 'Asia/Kolkata',
  UTC: 'UTC',
  EST: 'America/New_York',
  PST: 'America/Los_Angeles',
  GMT: 'Europe/London',
  JST: 'Asia/Tokyo',
  AEST: 'Australia/Sydney',
  // Browser default
  AUTO: null
};

/**
 * Format date to local date string (e.g., "12/7/2025")
 * @param {Date|string} date - Date object or ISO string
 * @param {object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  const timezone = getUserTimezone();

  const defaultOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: timezone
  };

  return dateObj.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format date to long format (e.g., "Thursday, December 7, 2025")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string
 */
export const formatDateLong = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  const timezone = getUserTimezone();

  return dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timezone
  });
};

/**
 * Format date to short format (e.g., "Sun, Dec 7, 2025")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string
 */
export const formatDateShort = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  const timezone = getUserTimezone();

  return dateObj.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone
  });
};

/**
 * Format date and time (e.g., "12/7/2025, 3:30 PM")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  const timezone = getUserTimezone();

  return dateObj.toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  });
};

/**
 * Format date and time in long format (e.g., "December 7, 2025 at 3:30 PM IST")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date and time string
 */
export const formatDateTimeLong = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  const timezone = getUserTimezone();

  const formattedDateTime = dateObj.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  });

  // Get timezone abbreviation
  const timeZoneShort = dateObj.toLocaleString('en-US', {
    timeZoneName: 'short',
    timeZone: timezone
  }).split(' ').pop();

  return `${formattedDateTime} ${timeZoneShort}`;
};

/**
 * Format time only (e.g., "3:30 PM")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  const timezone = getUserTimezone();

  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  });
};

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  const now = new Date();
  const diffMs = dateObj - now;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (Math.abs(diffSecs) < 60) {
    return 'just now';
  } else if (Math.abs(diffMins) < 60) {
    const mins = Math.abs(diffMins);
    return diffMins > 0 ? `in ${mins} minute${mins > 1 ? 's' : ''}` : `${mins} minute${mins > 1 ? 's' : ''} ago`;
  } else if (Math.abs(diffHours) < 24) {
    const hours = Math.abs(diffHours);
    return diffHours > 0 ? `in ${hours} hour${hours > 1 ? 's' : ''}` : `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (Math.abs(diffDays) < 7) {
    const days = Math.abs(diffDays);
    return diffDays > 0 ? `in ${days} day${days > 1 ? 's' : ''}` : `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return formatDate(dateObj);
  }
};

/**
 * Parse a date string as local date (avoids timezone issues)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date|null} Date object or null if invalid
 */
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;

  // If it's already a Date object, return it
  if (dateStr instanceof Date) return dateStr;

  if (typeof dateStr === 'string') {
    // Handle ISO datetime format (2025-12-07T00:00:00 or 2025-12-07T00:00:00.000Z)
    // Extract just the date part before 'T'
    let dateOnly = dateStr;
    if (dateStr.includes('T')) {
      dateOnly = dateStr.split('T')[0];
    }

    // Parse YYYY-MM-DD format as local date
    if (dateOnly.includes('-')) {
      const parts = dateOnly.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          return new Date(year, month - 1, day);
        }
      }
    }
  }

  // Fallback: try to create date from the value
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
  return null;
};

/**
 * Check if a date is today
 * @param {Date|string} date - Date object or ISO string
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  if (!date) return false;

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return false;

  const today = new Date();
  const timezone = getUserTimezone();

  const dateStr = dateObj.toLocaleDateString('en-US', { timeZone: timezone });
  const todayStr = today.toLocaleDateString('en-US', { timeZone: timezone });

  return dateStr === todayStr;
};

/**
 * Check if a date is in the past
 * @param {Date|string} date - Date object or ISO string
 * @returns {boolean} True if date is in the past
 */
export const isPast = (date) => {
  if (!date) return false;

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return false;

  return dateObj < new Date();
};

/**
 * Check if a date is in the future
 * @param {Date|string} date - Date object or ISO string
 * @returns {boolean} True if date is in the future
 */
export const isFuture = (date) => {
  if (!date) return false;

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return false;

  return dateObj > new Date();
};

/**
 * Get days until a future date
 * @param {Date|string} date - Date object or ISO string
 * @returns {number} Number of days until the date (negative if past)
 */
export const daysUntil = (date) => {
  if (!date) return 0;

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return 0;

  const now = new Date();
  const diffMs = dateObj - now;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Get current timezone name and offset
 * @returns {object} { name: string, offset: string }
 */
export const getTimezoneInfo = () => {
  const timezone = getUserTimezone();
  const now = new Date();

  // Get timezone abbreviation
  const timeZoneShort = now.toLocaleString('en-US', {
    timeZoneName: 'short',
    timeZone: timezone
  }).split(' ').pop();

  // Get offset in hours
  const offsetMinutes = -now.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  const offsetSign = offsetMinutes >= 0 ? '+' : '-';
  const offset = `UTC${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;

  return {
    name: timeZoneShort,
    offset: offset,
    timezone: timezone
  };
};

export default {
  getUserTimezone,
  setUserTimezone,
  TIMEZONES,
  formatDate,
  formatDateLong,
  formatDateShort,
  formatDateTime,
  formatDateTimeLong,
  formatTime,
  formatRelativeTime,
  parseLocalDate,
  isToday,
  isPast,
  isFuture,
  daysUntil,
  getTimezoneInfo
};
